/**
 * Parses non-blocking server warnings returned with HTTP 2xx sync upload responses.
 */

function asTrimmedStrings(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
    .filter((s) => s.length > 0);
}

/** Extract human-readable warnings from an already-parsed JSON value (typically the POST response body). */
export function extractSyncUploadWarnings(parsedBody: unknown): string[] {
  if (parsedBody === null || parsedBody === undefined) return [];
  if (typeof parsedBody !== 'object' || Array.isArray(parsedBody)) return [];
  const o = parsedBody as Record<string, unknown>;
  const w = o.warnings ?? o.advertencias;
  const fromArray = asTrimmedStrings(w);
  if (fromArray.length > 0) return fromArray;
  const single =
    typeof w === 'string'
      ? w.trim()
      : typeof o.warning === 'string'
        ? o.warning.trim()
        : typeof o.advertencia === 'string'
          ? o.advertencia.trim()
          : '';
  return single ? [single] : [];
}

function asFiniteIntMeta(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    return Number.isFinite(v) && Math.trunc(v) === v ? Math.trunc(v) : null;
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return null;
    if (!/^[-+]?\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
}

/** Optional numeric fields surfaced on HTTP 2xx ingest responses (`id_sync` / postulation year aliases). */
export interface SyncUploadMeta {
  readonly idSync: number | null;
  readonly yearPostulacion: number | null;
}

/** Reads `idSync` / `yearpostulacion` (and snake_case aliases) from a parsed ingest JSON body. */
export function extractSyncUploadMeta(parsedBody: unknown): SyncUploadMeta {
  if (parsedBody === null || parsedBody === undefined) return { idSync: null, yearPostulacion: null };
  if (typeof parsedBody !== 'object' || Array.isArray(parsedBody)) return { idSync: null, yearPostulacion: null };
  const o = parsedBody as Record<string, unknown>;
  const idSync = asFiniteIntMeta(o.idSync ?? o.id_sync);
  const yearPostulacion = asFiniteIntMeta(
    o.yearpostulacion ?? o.yearPostulacion ?? o.year_postulacion,
  );
  return { idSync, yearPostulacion };
}

/** Parses a response body string as JSON when it looks plausible; swallow parse errors for optional metadata. */
export function tryParseSyncUploadBodyJson(raw: string): unknown {
  const t = raw.trim();
  if (t === '') return undefined;
  if (!((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']')))) return undefined;
  try {
    return JSON.parse(t) as unknown;
  } catch {
    return undefined;
  }
}
